import { useEffect, useState, type RefObject } from 'react'

// Flies the pick-flash card into the grid cell it belongs to, and reports back
// where that cell is so the overlay can draw its own flare there.
//
// The cell is already filled and already on screen — the reducer folds the pick
// before the flash mounts — so this is not a handoff. The scrim fades, the card
// lands on top of the real cell at the same size, at zero opacity. Nothing swaps.
//
// The flare is *not* a class on the cell. PickCell's className is fully
// controlled, so any class added here is wiped the next time React re-renders
// that cell — which happens whenever another pick lands. The overlay draws the
// ring itself, at the rect this returns.

const DURATION_MS = 600
// The tail of the flight, over which the cell takes over from the card.
const REVEAL_MS = 220

export interface FlightRect {
  left: number
  top: number
  width: number
  height: number
}

export function useFlightToCell(
  cardRef: RefObject<HTMLElement | null>,
  overall: number,
  delayMs: number,
): FlightRect | null {
  const [rect, setRect] = useState<FlightRect | null>(null)

  useEffect(() => {
    const card = cardRef.current
    if (!card) return

    // The global prefers-reduced-motion rule is a CSS media query and does not
    // reach the Web Animations API. Bail to the plain fade the wrapper already
    // does, and draw no flare.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const cell = document.querySelector<HTMLElement>(`[data-overall="${overall}"]`)
    if (!cell) return

    const to = cell.getBoundingClientRect()
    if (to.width === 0 || to.height === 0) return

    // Measure the card untransformed. offsetWidth/offsetHeight ignore the
    // entrance scale that is mid-flight on this very element; a
    // getBoundingClientRect here would fold in whatever frame it happens to be
    // on and the flight would land at the wrong size. The card is flex-centred
    // in a `fixed inset-0` overlay, so its resting centre is the viewport's.
    const scale = to.width / card.offsetWidth
    const dx = to.left + to.width / 2 - window.innerWidth / 2
    const dy = to.top + to.height / 2 - window.innerHeight / 2

    const flight = card.animate(
      [
        { transform: 'none', opacity: 1 },
        {
          transform: `translate(${dx * 0.62}px, ${dy * 0.44}px) scale(${scale + (1 - scale) * 0.34})`,
          opacity: 0.92,
          offset: 0.55,
        },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 },
      ],
      // forwards, never both: `both` applies the first frame through the delay
      // and would hold the card at full opacity from t=0, overriding the
      // entrance beats underneath it.
      { duration: DURATION_MS, delay: delayMs, easing: 'cubic-bezier(.55,0,.3,1)', fill: 'forwards' },
    )

    // Hold the destination cell back until the card lands on it.
    //
    // The cell is filled before this hook ever runs — the reducer folds the
    // pick, PickGrid renders it, and the flash fires off the same state change.
    // So the answer is sitting in the grid the entire time the drum roll is
    // building, and the moment the scrim clears it is readable *before* the card
    // arrives, which gives away the reveal the whole sequence exists to set up.
    //
    // fill: 'both' is right here and nowhere else in this sequence. Everywhere
    // else it is the bug — an entry applying through its own delay silently eats
    // whatever came before it. Here that is precisely the point: the 0% frame
    // has to hold the cell hidden from t=0 through the delay, and only then hand
    // over. The two cross-fade over the flight's last 220ms, so the card
    // dissolves into a cell that is coming up underneath it.
    const reveal = cell.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: REVEAL_MS,
      delay: delayMs + DURATION_MS - REVEAL_MS,
      fill: 'both',
    })

    setRect({ left: to.left, top: to.top, width: to.width, height: to.height })

    return () => {
      flight.cancel()
      // Not optional: a pick landing mid-flight tears this hook down, and an
      // uncancelled reveal would leave its predecessor's cell hidden for good.
      reveal.cancel()
      setRect(null)
    }
  }, [cardRef, overall, delayMs])

  return rect
}
